import React, { useState } from 'react';
import dataService from '../services/dataService';
import censusApiService from '../services/censusApiService';

const UnifiedDataExplorer = ({ onMetricSelect, selectedMetric }) => {
    const [expandedCategories, setExpandedCategories] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [dataSource, setDataSource] = useState('all'); // all, custom, census
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState('categories'); // categories, search, favorites
    const [favorites, setFavorites] = useState(() => {
        const saved = localStorage.getItem('favoriteMetrics');
        return saved ? JSON.parse(saved) : [];
    });

    // Combine data sources into unified structure
    const getUnifiedCategories = () => {
        const categories = {};
        
        // Add custom data categories
        const customCategories = dataService.getDataTypesByCategory();
        Object.entries(customCategories).forEach(([category, metrics]) => {
            if (!categories[category]) categories[category] = { metrics: [], source: 'mixed' };
            metrics.forEach(metric => {
                categories[category].metrics.push({
                    key: metric.key,
                    name: metric.name,
                    description: metric.description,
                    source: 'custom',
                    unit: metric.unit,
                    available: true
                });
            });
        });
        
        // Add census data categories if API key is configured
        const censusConfigured = process.env.REACT_APP_CENSUS_API_KEY && process.env.REACT_APP_CENSUS_API_KEY !== '';
        if (censusConfigured) {
            const censusData = censusApiService.datasets.acs5.variables;
            Object.entries(censusData).forEach(([category, variables]) => {
                const formattedCategory = category.charAt(0).toUpperCase() + category.slice(1);
                if (!categories[formattedCategory]) categories[formattedCategory] = { metrics: [], source: 'census' };
                
                // Add top census variables for each category (limit for better UX)
                const topVariables = Object.entries(variables).slice(0, 10);
                topVariables.forEach(([code, description]) => {
                    categories[formattedCategory].metrics.push({
                        key: `census_${code}`,
                        name: description,
                        description: `Census Bureau: ${description}`,
                        source: 'census',
                        code: code,
                        available: true
                    });
                });
                
                // Add "View More" option if there are more variables
                if (Object.keys(variables).length > 10) {
                    categories[formattedCategory].hasMore = true;
                    categories[formattedCategory].totalCount = Object.keys(variables).length;
                }
            });
        }
        
        return categories;
    };

    // Search functionality
    const searchMetrics = () => {
        if (!searchTerm) return {};
        
        const results = {};
        const categories = getUnifiedCategories();
        
        Object.entries(categories).forEach(([categoryName, categoryData]) => {
            const matchingMetrics = categoryData.metrics.filter(metric => 
                metric.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                metric.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
            
            if (matchingMetrics.length > 0) {
                results[categoryName] = {
                    ...categoryData,
                    metrics: matchingMetrics
                };
            }
        });
        
        return results;
    };

    // Toggle favorite
    const toggleFavorite = (metricKey) => {
        const newFavorites = favorites.includes(metricKey)
            ? favorites.filter(f => f !== metricKey)
            : [...favorites, metricKey];
        
        setFavorites(newFavorites);
        localStorage.setItem('favoriteMetrics', JSON.stringify(newFavorites));
    };

    // Get category icon
    const getCategoryIcon = (category) => {
        const icons = {
            'Economics': '💰',
            'Demographics': '👥',
            'Education': '🎓',
            'Health': '🏥',
            'Housing': '🏠',
            'Crime & Safety': '⚖️',
            'Environment': '🌍',
            'Infrastructure': '🛤️',
            'Government': '🏛️',
            'Employment': '💼',
            'Social': '🤝',
            'Transportation': '🚗'
        };
        return icons[category] || '📊';
    };


    // Toggle category expansion
    const toggleCategory = (categoryName) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryName]: !prev[categoryName]
        }));
    };

    // Handle metric selection
    const handleMetricClick = async (metric) => {
        setLoading(true);
        
        try {
            if (metric.source === 'census') {
                // Handle census data fetch
                const data = await censusApiService.getStateData('acs5', 2022, [metric.code]);
                onMetricSelect({ 
                    key: metric.key, 
                    data, 
                    source: 'census',
                    description: metric.name 
                });
            } else {
                // Handle custom data
                onMetricSelect(metric.key);
            }
        } catch (error) {
            console.error('Error loading metric:', error);
        } finally {
            setLoading(false);
        }
    };

    // Get filtered categories based on data source
    const getFilteredCategories = () => {
        const allCategories = searchTerm ? searchMetrics() : getUnifiedCategories();
        
        if (dataSource === 'all') return allCategories;
        
        const filtered = {};
        Object.entries(allCategories).forEach(([category, data]) => {
            const filteredMetrics = data.metrics.filter(m => 
                dataSource === 'census' ? m.source === 'census' : m.source === 'custom'
            );
            
            if (filteredMetrics.length > 0) {
                filtered[category] = { ...data, metrics: filteredMetrics };
            }
        });
        
        return filtered;
    };

    const categories = getFilteredCategories();
    const totalMetrics = Object.values(categories).reduce((sum, cat) => sum + cat.metrics.length, 0);

    return (
        <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f8f9fa'
        }}>
            {/* Header */}
            <div style={{
                padding: '16px',
                backgroundColor: 'white',
                borderBottom: '1px solid #e0e0e0'
            }}>
                <h3 style={{
                    margin: '0 0 12px 0',
                    fontSize: '18px',
                    color: '#2C3E50',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    📊 Data Explorer
                    <span style={{
                        fontSize: '12px',
                        padding: '2px 8px',
                        backgroundColor: '#E3F2FD',
                        color: '#1976D2',
                        borderRadius: '12px'
                    }}>
                        {totalMetrics} metrics
                    </span>
                </h3>

                {/* Search Bar */}
                <div style={{ marginBottom: '12px' }}>
                    <input
                        type="text"
                        placeholder="Search metrics (e.g., income, education, poverty)..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    />
                </div>

                {/* Data Source Filter */}
                <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '12px'
                }}>
                    {[
                        { value: 'all', label: 'All Sources', icon: '🔄' },
                        { value: 'custom', label: 'Curated', icon: '⭐' },
                        { value: 'census', label: 'Census', icon: '🏛️' }
                    ].map(source => (
                        <button
                            key={source.value}
                            onClick={() => setDataSource(source.value)}
                            style={{
                                flex: 1,
                                padding: '6px',
                                backgroundColor: dataSource === source.value ? '#4CAF50' : 'white',
                                color: dataSource === source.value ? 'white' : '#666',
                                border: `1px solid ${dataSource === source.value ? '#4CAF50' : '#ddd'}`,
                                borderRadius: '4px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px'
                            }}
                        >
                            <span>{source.icon}</span>
                            <span>{source.label}</span>
                        </button>
                    ))}
                </div>

                {/* View Mode Tabs */}
                <div style={{
                    display: 'flex',
                    gap: '4px'
                }}>
                    {[
                        { value: 'categories', label: 'Categories', icon: '📁' },
                        { value: 'favorites', label: `Favorites (${favorites.length})`, icon: '⭐' },
                        { value: 'recent', label: 'Recent', icon: '🕒' }
                    ].map(mode => (
                        <button
                            key={mode.value}
                            onClick={() => setViewMode(mode.value)}
                            style={{
                                flex: 1,
                                padding: '6px',
                                backgroundColor: viewMode === mode.value ? '#f0f0f0' : 'transparent',
                                border: 'none',
                                borderBottom: viewMode === mode.value ? '2px solid #4CAF50' : '2px solid transparent',
                                fontSize: '12px',
                                cursor: 'pointer',
                                color: viewMode === mode.value ? '#4CAF50' : '#666'
                            }}
                        >
                            {mode.icon} {mode.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content Area */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '8px'
            }}>
                {loading && (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                        Loading metrics...
                    </div>
                )}

                {/* Categories View */}
                {viewMode === 'categories' && Object.entries(categories).map(([categoryName, categoryData]) => {
                    const isExpanded = expandedCategories[categoryName];
                    
                    return (
                        <div key={categoryName} style={{
                            marginBottom: '8px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0',
                            overflow: 'hidden'
                        }}>
                            <div
                                onClick={() => toggleCategory(categoryName)}
                                style={{
                                    padding: '12px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    backgroundColor: isExpanded ? '#fafafa' : 'white',
                                    borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '20px' }}>
                                        {getCategoryIcon(categoryName)}
                                    </span>
                                    <div>
                                        <div style={{
                                            fontWeight: '500',
                                            fontSize: '14px',
                                            color: '#2C3E50'
                                        }}>
                                            {categoryName}
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', marginTop: '2px' }}>
                                            <span style={{
                                                fontSize: '11px',
                                                padding: '1px 6px',
                                                backgroundColor: '#e3f2fd',
                                                color: '#1976d2',
                                                borderRadius: '10px'
                                            }}>
                                                {categoryData.metrics.length} metrics
                                            </span>
                                            {categoryData.hasMore && (
                                                <span style={{
                                                    fontSize: '11px',
                                                    padding: '1px 6px',
                                                    backgroundColor: '#fff3e0',
                                                    color: '#ff9800',
                                                    borderRadius: '10px'
                                                }}>
                                                    +{categoryData.totalCount - categoryData.metrics.length} more
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <span style={{ fontSize: '12px', color: '#999' }}>
                                    {isExpanded ? '▼' : '▶'}
                                </span>
                            </div>

                            {isExpanded && (
                                <div style={{
                                    maxHeight: '400px',
                                    overflowY: 'auto'
                                }}>
                                    {categoryData.metrics.map((metric) => {
                                        const isSelected = selectedMetric === metric.key;
                                        const isFavorite = favorites.includes(metric.key);
                                        
                                        return (
                                            <div
                                                key={metric.key}
                                                style={{
                                                    padding: '10px 12px',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    backgroundColor: isSelected ? '#e8f5e9' : 'white',
                                                    borderLeft: isSelected ? '3px solid #4CAF50' : '3px solid transparent',
                                                    borderBottom: '1px solid #f5f5f5'
                                                }}
                                                onClick={() => handleMetricClick(metric)}
                                            >
                                                <div style={{ flex: 1 }}>
                                                    <div style={{
                                                        fontSize: '13px',
                                                        fontWeight: isSelected ? '500' : 'normal',
                                                        color: isSelected ? '#2E7D32' : '#333'
                                                    }}>
                                                        {metric.name}
                                                    </div>
                                                    {metric.unit && (
                                                        <div style={{
                                                            fontSize: '11px',
                                                            color: '#999',
                                                            marginTop: '2px'
                                                        }}>
                                                            Unit: {metric.unit}
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                    <span style={{
                                                        fontSize: '10px',
                                                        padding: '2px 6px',
                                                        backgroundColor: metric.source === 'census' ? '#E3F2FD' : '#E8F5E9',
                                                        color: metric.source === 'census' ? '#1976D2' : '#4CAF50',
                                                        borderRadius: '4px',
                                                        fontWeight: '500'
                                                    }}>
                                                        {metric.source === 'census' ? '🏛️ Census' : '⭐ Curated'}
                                                    </span>
                                                    
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleFavorite(metric.key);
                                                        }}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            fontSize: '16px',
                                                            padding: '0',
                                                            color: isFavorite ? '#FFD700' : '#ddd'
                                                        }}
                                                    >
                                                        {isFavorite ? '⭐' : '☆'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    
                                    {categoryData.hasMore && (
                                        <div style={{
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#f8f9fa',
                                            borderTop: '1px solid #e0e0e0'
                                        }}>
                                            <button
                                                onClick={() => alert('Open advanced census data selector')}
                                                style={{
                                                    padding: '6px 16px',
                                                    backgroundColor: '#1976D2',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                View All {categoryData.totalCount} Variables →
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Favorites View */}
                {viewMode === 'favorites' && (
                    <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px' }}>
                        {favorites.length > 0 ? (
                            <div>
                                <h4 style={{ marginBottom: '12px', color: '#333' }}>Your Favorite Metrics</h4>
                                {favorites.map(fav => (
                                    <div key={fav} style={{
                                        padding: '8px',
                                        marginBottom: '8px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <span>{fav}</span>
                                        <button
                                            onClick={() => toggleFavorite(fav)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: '#DC143C'
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
                                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⭐</div>
                                <p>No favorites yet!</p>
                                <p style={{ fontSize: '14px' }}>
                                    Click the star icon next to any metric to save it here for quick access.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* No results message */}
                {Object.keys(categories).length === 0 && searchTerm && (
                    <div style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: '#999'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                        <p>No metrics found for "{searchTerm}"</p>
                        <button
                            onClick={() => setSearchTerm('')}
                            style={{
                                marginTop: '12px',
                                padding: '8px 16px',
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Clear Search
                        </button>
                    </div>
                )}
            </div>

            {/* Footer Stats */}
            <div style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                borderTop: '1px solid #e0e0e0',
                fontSize: '11px',
                color: '#666',
                display: 'flex',
                justifyContent: 'space-between'
            }}>
                <span>📊 {totalMetrics} metrics available</span>
                <span>🏛️ Census API: {process.env.REACT_APP_CENSUS_API_KEY ? '✅' : '⚠️'}</span>
            </div>
        </div>
    );
};

export default UnifiedDataExplorer;